import ClaimsDialog from "../ingestion/ClaimsDialog";
import AggregatedClaimsInput from "./AggregatedClaimsInput";
import AggregatedClaimsTable from "./AggregatedClaimsTable";
import ClaimsAndDeltaChart from "./ClaimsAndDeltaChart";
import ClaimsChart from "./ClaimsChart";
import ClaimsChartAggregateLossByLine from "./ClaimsChartAggregateLossByLine";
import ClaimsComparisonTable from "./ClaimsComparisonTable";
import ClaimsDeltaChart from "./ClaimsDeltaChart";
import ClaimsDownloader from "./ClaimsDownloader";
import ClaimsLargest from "./ClaimsLargest";
import ClaimsMerger from "./ClaimsMerger";
import ClaimsTable from "./ClaimsTable";
import ConfiguredClaimsFields from "./ConfiguredClaimsFields";
import FilteredClaims from "./FilteredClaims";
import ManualClaims from "./ManualClaims";
import SelfInsuredRetentions from "./SelfInsuredRetentions";
import Disabled from "components/common/Disabled";
import Notes from "components/common/Notes";
import Visible from "components/common/Visible";
import * as labels from "config.labels";
import * as claims from "domain/claims";
import {
  CREATE_CLAIMS_COVERAGE_LIST,
  INCLUDE_AGGREGATE_LOSS_BY_LINE,
} from "flags";
import { useState } from "react";
import { connect, useSelector } from "react-redux";
import * as claimsService from "services/claimsService";
import * as claimsActions from "store/actions/input/claims/claimsActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const SummaryCharts = ({ claimsQuery }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const { currentData, isFetching, error } = claimsService.useSummaryChartQuery(
    claimsQuery
  );
  return (
    <>
      <ClaimsChart
        title={translateLabel(labels.LOSS_FREQUENCY_TITLE, {
          default: "Loss Frequency",
        })}
        subtitle={translateLabel(labels.LOSS_FREQUENCY_SUBTITLE, {
          default: "Number of losses per year",
        })}
        chart={currentData?.frequency}
        loading={isFetching}
        error={error}
        dataName={translateLabel(labels.FREQUENCY_DATA_NAME, {
          default: "Frequency",
        })}
      />
      <ClaimsChart
        title={translateLabel(labels.LOSS_SEVERITY_TITLE, {
          default: "Loss Severity",
        })}
        subtitle={translateLabel(labels.LOSS_SEVERITY_SUBTITLE, {
          default: "Maximum loss in a year",
        })}
        chart={currentData?.severity}
        dataName={translateLabel(labels.SEVERITY_DATA_NAME, {
          default: "Severity",
        })}
        loading={isFetching}
        error={error}
        ultimateName={translateLabel(labels.DEVELOPMENT_AND_INFLATION, {
          default: "Development & Inflation",
        })}
      />
      <ClaimsChart
        title={translateLabel(labels.LOSS_AGGREGATE_TITLE, {
          default: "Loss Aggregate",
        })}
        subtitle={translateLabel(labels.LOSS_AGGREGATE_SUBTITLE, {
          default: "Total loss per year",
        })}
        chart={currentData?.aggregate}
        loading={isFetching}
        error={error}
        dataName={translateLabel(labels.AGGREGATE_DATA_NAME, {
          default: "Aggregate",
        })}
        ultimateName={translateLabel(labels.DEVELOPMENT_AND_INFLATION, {
          default: "Development & Inflation",
        })}
      />
    </>
  );
};

const AllClaimsChart = ({ claimsQuery }) => {
  const claimsAbove = useSelector(claimsSelectors.selectClaimsThreshold);
  const {
    currentData: allChartData,
    error,
  } = claimsService.useAllClaimsChartQuery({
    claimsQuery,
    claimsAbove,
  });
  return (
    <FilteredClaims
      data={allChartData}
      error={error}
      disabled
      zoomIdentifier={"claims"}
    />
  );
};

export const ClaimsComponent = (props) => {
  const inputClaimsQuery = useDebouncedValue(
    useSelector(claimsSelectors.selectInputClaims)
  );

  const usingAggregatedClaimsInput =
    useSelector(claimsSelectors.selectAggregatedClaims).manualMode ?? false;
  const hasClaimsToShow =
    !usingAggregatedClaimsInput && !claims.isClaimsQueryEmpty(inputClaimsQuery);

  const createCoverageList =
    props.componentVisibility?.[CREATE_CLAIMS_COVERAGE_LIST] ?? true;

  const [claimsComparisonQuery, setClaimsComparisonQuery] = useState({
    primaryMatch: "_DATE_OF_LOSS",
    requiredMatchColumns: ["_COVERAGE", "_CLAIMANT_NAME"],
    matchColumns: ["_DESCRIPTION", "_OPEN_CLOSED"],
    textColumns: [
      "_COVERAGE",
      "_CLAIMANT_NAME",
      "_DESCRIPTION",
      "_OPEN_CLOSED",
    ],
    numericColumns: ["_TOTAL_LOSS"],
    limit: 100,
  });

  const [mergingClaims, setMergingClaims] = useState(false);
  const [claimsState, setClaimsState] = useState({});
  const toggleMergingClaims = () => {
    setMergingClaims(!mergingClaims);
    setClaimsState({});
  };
  const stopMerging = () => {
    setMergingClaims(false);
    setClaimsState({});
  };

  const merge = () => {
    const merger = new ClaimsMerger();
    merger.doMerge(
      claimsState,
      props.toggleClaimExclude,
      props.createMergedClaim,
      stopMerging
    );
  };
  const rowToKey = (row) => row.key + ":" + row.index;

  const setMergeStateEntry = (row, selected) => {
    const entry = rowToKey(row);
    const newClaimsState = {
      ...claimsState,
      [entry]: { row, selected },
    };
    setClaimsState(newClaimsState);
  };
  const mergeEntrySelected = (row) => {
    const entry = rowToKey(row);
    if (entry in claimsState) {
      return claimsState[entry].selected;
    }
    return false;
  };
  return (
    <>
      {!hasClaimsToShow && (
        <Visible
          byTag={"input.claims.aggregatedClaimsInput"}
          defaultVisible={usingAggregatedClaimsInput}
        >
          <AggregatedClaimsInput />
        </Visible>
      )}

      {hasClaimsToShow && (
        <>
          <SummaryCharts claimsQuery={inputClaimsQuery} />
          <Visible byTag={INCLUDE_AGGREGATE_LOSS_BY_LINE}>
            <ClaimsChartAggregateLossByLine claimsQuery={inputClaimsQuery} />
          </Visible>
          <AllClaimsChart claimsQuery={inputClaimsQuery} />
          <Visible
            byTag={"input.claims.aggregatedClaimsTable"}
            defaultVisible={false}
          >
            <AggregatedClaimsTable />
          </Visible>
        </>
      )}

      {!usingAggregatedClaimsInput && (
        <>
          <Visible
            byTag={"input.claims.selfInsuredRetentions"}
            defaultVisible={false}
          >
            <SelfInsuredRetentions />
          </Visible>
          <Disabled ifReadOnly>
            <ClaimsDialog />
          </Disabled>
          <ClaimsDownloader />
          <ManualClaims
            {...{ mergingClaims, toggleMergingClaims, createCoverageList }}
          />
        </>
      )}
      {hasClaimsToShow && (
        <ClaimsTable
          {...{
            mergingClaims,
            mergeEntrySelected,
            setMergeStateEntry,
            merge,
            createCoverageList,
          }}
        />
      )}
      {!usingAggregatedClaimsInput && (
        <>
          <Visible
            byTag={"input.claims.claimsAndDeltaChart"}
            defaultVisible={false}
          >
            <ClaimsAndDeltaChart
              query={claimsComparisonQuery}
              setQuery={setClaimsComparisonQuery}
            />
          </Visible>
          <Visible
            byTag={"input.claims.claimsDeltaChart"}
            defaultVisible={false}
          >
            <ClaimsDeltaChart
              query={claimsComparisonQuery}
              setQuery={setClaimsComparisonQuery}
            />
          </Visible>
          <Visible
            byTag={"input.claims.claimsComparisonTable"}
            defaultVisible={false}
          >
            <ClaimsComparisonTable
              query={claimsComparisonQuery}
              setQuery={setClaimsComparisonQuery}
            />
          </Visible>
        </>
      )}
      <Notes
        md_width={12}
        lg_width={6}
        update={props.updateNote}
        value={props.note}
        header={<ConfiguredClaimsFields />}
      />
      {hasClaimsToShow && (
        <Visible byTag={"input.claims.claimsLargest"} defaultVisible={true}>
          <ClaimsLargest />
        </Visible>
      )}
    </>
  );
};

const mapDispatchToProps = {
  toggleClaimExclude: claimsActions.toggleClaimExclude,
  updateNote: claimsActions.updateClaimsNote,
  claimsFileChecked: claimsActions.claimsFileChecked,
  createMergedClaim: claimsActions.createManualClaim,
};

const mapStateToProps = (state) => ({
  ...state.input.claims,
  componentVisibility: userSelectors.selectUserConfig(state)
    .componentVisibility,
});

export default connect(mapStateToProps, mapDispatchToProps)(ClaimsComponent);
